import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, ExternalLink, X } from "lucide-react";

import { entities as entitiesApi, signals as signalsApi } from "@/lib/api";
import { DomainAwareEmpty } from "@/components/canvas/empty-hint";
import { useTimeRange } from "@/lib/use-time-range";

const TYPE_COLORS: Record<string, string> = {
  incident_report: "var(--color-risk-high)",
  news_article: "var(--color-primary)",
  displacement_event: "var(--color-risk-medium)",
  military_communique: "oklch(0.65 0.20 305)",
  prediction: "#a855f7",
  arrest: "oklch(0.62 0.18 200)",
  default: "var(--color-risk-low)",
};

function colorFor(type: string): string {
  return TYPE_COLORS[type] ?? TYPE_COLORS.default;
}

type GroupBy = "type" | "entity";

type Signal = {
  id: string;
  type: string;
  timestamp: string;
  related_entity_ids: string[];
  attributes: Record<string, unknown>;
};

// Grid geometry — must match the Tailwind classes on the track row.
const LABEL_COL_PX = 150;
const GRID_GAP_PX = 12;
const TRACK_H = 34;
// Target pixels per bucket. Buckets narrower than this stop being clickable
// and start aliasing; wider than this and we lose temporal resolution.
const PX_PER_BUCKET = 11;
// Upper bound on rendered tracks — see the note on the tracks memo. 40 × 140
// buckets is ~5.6k nodes worst case, which the browser handles comfortably.
const MAX_TRACKS = 40;

/**
 * LiveTimelineCanvas — density timeline.
 *
 * Every previous iteration drew one dot per signal, which does not survive
 * real workspaces: 500 signals inside a two-week window produced dozens of
 * stacked rows per track and read as solid blobs. Instead we bucket time
 * into fixed columns and draw a small histogram per track, so a track is
 * always exactly TRACK_H tall whether it holds 20 signals or 20,000.
 * Drilling down is the panel's job: click a bucket to list its signals,
 * click a signal for full detail.
 */
export function LiveTimelineCanvas({ workspaceId }: { workspaceId: string }) {
  const [groupBy, setGroupBy] = useState<GroupBy>("type");
  // Panel state: a selected bucket (list of signals) and, within it, an
  // optionally selected signal (full detail).
  const [selectedBucket, setSelectedBucket] = useState<{
    trackKey: string;
    trackLabel: string;
    index: number;
  } | null>(null);
  const [selectedSignalId, setSelectedSignalId] = useState<string | null>(null);
  const tr = useTimeRange();

  const sigs = useQuery({
    queryKey: ["signals", workspaceId],
    queryFn: () => signalsApi.list(workspaceId),
    enabled: Boolean(workspaceId),
  });
  const ents = useQuery({
    queryKey: ["entities", workspaceId],
    queryFn: () => entitiesApi.list(workspaceId),
    enabled: Boolean(workspaceId),
  });

  const rawData = sigs.data ?? [];
  const data = useMemo<Signal[]>(
    () => (tr.isActive ? rawData.filter((s) => tr.inRange(s.timestamp)) : rawData),
    [rawData, tr],
  );
  const entityNames = useMemo(
    () => Object.fromEntries((ents.data ?? []).map((e) => [e.id, e.name])),
    [ents.data],
  );

  // Measure the real strip width so bucket count matches what's on screen.
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [stripWidth, setStripWidth] = useState(760);
  const hasData = data.length > 0;
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const measure = (w: number) =>
      setStripWidth(Math.max(160, w - LABEL_COL_PX - GRID_GAP_PX));
    measure(el.getBoundingClientRect().width);
    const ro = new ResizeObserver(([e]) => measure(e.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, [hasData]);

  const [minTs, maxTs] = useMemo(() => {
    if (data.length === 0) {
      const now = Date.now();
      return [now - 30 * 86400_000, now];
    }
    const ts = data.map((s) => new Date(s.timestamp).getTime());
    const minT = Math.min(...ts);
    const maxT = Math.max(...ts);
    // Guard the degenerate single-timestamp case so bucketMs is never 0.
    if (maxT === minT) return [minT - 43200_000, maxT + 43200_000];
    return [minT, maxT];
  }, [data]);

  const bucketCount = Math.max(
    12,
    Math.min(140, Math.round(stripWidth / PX_PER_BUCKET)),
  );
  const bucketMs = (maxTs - minTs) / bucketCount;

  // One pass over the data: group into tracks, and inside each track bucket
  // by time. Buckets hold the signals themselves so the panel can list them
  // without a second scan.
  //
  // Tracks are capped. Each rendered track is `bucketCount` interactive
  // columns (up to 140), so the DOM cost is tracks × buckets: fine for
  // "group by type" (~20 types) but catastrophic for "group by entity",
  // where a real workspace has hundreds of entities — 300 × 140 would be
  // 42,000 nodes and would lock up the tab. We render the busiest
  // MAX_TRACKS and tell the operator what was left out.
  const { tracks, trackTotal } = useMemo(() => {
    if (data.length === 0) return { tracks: [], trackTotal: 0 };
    const groups = new Map<string, Signal[]>();
    for (const s of data) {
      if (groupBy === "type") {
        const arr = groups.get(s.type) ?? groups.set(s.type, []).get(s.type)!;
        arr.push(s);
      } else {
        const keys = s.related_entity_ids.length
          ? s.related_entity_ids
          : ["(unattached)"];
        for (const k of keys) {
          const arr = groups.get(k) ?? groups.set(k, []).get(k)!;
          arr.push(s);
        }
      }
    }
    // Sort before bucketing so we only pay the bucketing cost for tracks we
    // actually render.
    const ranked = [...groups.entries()].sort((a, b) => b[1].length - a[1].length);
    const built = ranked.slice(0, MAX_TRACKS).map(([key, items]) => {
      const buckets: Signal[][] = Array.from({ length: bucketCount }, () => []);
      for (const s of items) {
        const t = new Date(s.timestamp).getTime();
        let i = Math.floor((t - minTs) / bucketMs);
        if (i < 0) i = 0;
        if (i >= bucketCount) i = bucketCount - 1;
        buckets[i].push(s);
      }
      let peak = 0;
      for (const b of buckets) if (b.length > peak) peak = b.length;
      return {
        key,
        label: groupBy === "entity" ? entityNames[key] ?? key : key,
        total: items.length,
        buckets,
        peak,
      };
    });
    return { tracks: built, trackTotal: ranked.length };
  }, [data, groupBy, entityNames, bucketCount, bucketMs, minTs]);

  // Resolve the panel's contents from the current selection.
  const bucketSignals = useMemo<Signal[]>(() => {
    if (!selectedBucket) return [];
    const track = tracks.find((t) => t.key === selectedBucket.trackKey);
    const list = track?.buckets[selectedBucket.index] ?? [];
    return [...list].sort(
      (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
    );
  }, [selectedBucket, tracks]);
  const selectedSignal = useMemo<Signal | null>(
    () => bucketSignals.find((s) => s.id === selectedSignalId) ?? null,
    [bucketSignals, selectedSignalId],
  );

  // Selection can go stale when the grouping / window changes under it.
  useEffect(() => {
    setSelectedBucket(null);
    setSelectedSignalId(null);
  }, [groupBy, bucketCount]);

  const closePanel = () => {
    setSelectedBucket(null);
    setSelectedSignalId(null);
  };

  const axisTicks = useMemo(
    () => buildAxisTicks(minTs, maxTs, stripWidth),
    [minTs, maxTs, stripWidth],
  );

  return (
    <div className="relative h-full w-full overflow-hidden rounded-md bg-[oklch(0.14_0.01_260)] flex">
      <div className="flex-1 min-w-0 flex flex-col">
        <div className="flex items-center justify-between px-3 py-2 border-b border-border bg-surface/60 text-[11px]">
          <div className="flex items-center gap-4">
            <span className="text-foreground/80">
              <span className="font-mono text-foreground">{data.length}</span>{" "}
              <span className="text-muted-foreground">
                signals
                {tr.isActive && rawData.length !== data.length
                  ? ` of ${rawData.length}`
                  : ""}
              </span>
            </span>
            <div className="flex items-center gap-2 text-muted-foreground">
              <span>Group by</span>
              <button
                onClick={() => setGroupBy("type")}
                className={`px-2 py-0.5 rounded ${groupBy === "type" ? "bg-primary/20 text-primary" : "hover:text-foreground"}`}
              >
                type
              </button>
              <button
                onClick={() => setGroupBy("entity")}
                className={`px-2 py-0.5 rounded ${groupBy === "entity" ? "bg-primary/20 text-primary" : "hover:text-foreground"}`}
              >
                entity
              </button>
            </div>
            <span className="text-muted-foreground hidden lg:inline">
              {formatBucketSpan(bucketMs)} per column · click a column for detail
            </span>
          </div>
          {sigs.isLoading && <span className="text-muted-foreground">Loading…</span>}
        </div>

        {data.length === 0 && !sigs.isLoading ? (
          <div className="flex-1 relative">
            <DomainAwareEmpty workspaceId={workspaceId} kind="signal" />
          </div>
        ) : (
          <div className="flex-1 min-h-0 overflow-auto px-4 py-3">
            <div ref={wrapRef}>
              {/* Time axis */}
              <div
                className="grid items-end mb-1"
                style={{
                  gridTemplateColumns: `${LABEL_COL_PX}px minmax(0,1fr)`,
                  columnGap: `${GRID_GAP_PX}px`,
                }}
              >
                <div />
                <div className="relative h-4">
                  {axisTicks.map((t) => (
                    <span
                      key={t.ts}
                      className="absolute -translate-x-1/2 text-[9px] font-mono uppercase tracking-wider text-muted-foreground whitespace-nowrap"
                      style={{ left: `${t.pct}%` }}
                    >
                      {t.label}
                    </span>
                  ))}
                </div>
              </div>

              <div className="space-y-1.5">
                {tracks.map((track) => (
                  <Track
                    key={track.key}
                    track={track}
                    minTs={minTs}
                    bucketMs={bucketMs}
                    selectedIndex={
                      selectedBucket?.trackKey === track.key
                        ? selectedBucket.index
                        : null
                    }
                    onSelectBucket={(index) => {
                      setSelectedSignalId(null);
                      setSelectedBucket(
                        selectedBucket?.trackKey === track.key &&
                          selectedBucket.index === index
                          ? null
                          : { trackKey: track.key, trackLabel: track.label, index },
                      );
                    }}
                  />
                ))}
              </div>

              {trackTotal > tracks.length && (
                <div className="mt-2 text-[10px] text-muted-foreground">
                  Showing the {tracks.length} busiest of {trackTotal} — narrow
                  the time window or group by type to see the rest.
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {selectedBucket && (
        <SidePanel
          title={selectedBucket.trackLabel}
          rangeLabel={formatBucketRange(
            minTs + selectedBucket.index * bucketMs,
            minTs + (selectedBucket.index + 1) * bucketMs,
          )}
          signals={bucketSignals}
          selectedSignal={selectedSignal}
          entityNames={entityNames}
          onPick={setSelectedSignalId}
          onBack={() => setSelectedSignalId(null)}
          onClose={closePanel}
        />
      )}
    </div>
  );
}

function Track({
  track,
  minTs,
  bucketMs,
  selectedIndex,
  onSelectBucket,
}: {
  track: { key: string; label: string; total: number; buckets: Signal[][]; peak: number };
  minTs: number;
  bucketMs: number;
  selectedIndex: number | null;
  onSelectBucket: (index: number) => void;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const color = colorFor(track.key);
  const n = track.buckets.length;

  return (
    <div
      className="grid items-center"
      style={{
        gridTemplateColumns: `${LABEL_COL_PX}px minmax(0,1fr)`,
        columnGap: `${GRID_GAP_PX}px`,
      }}
    >
      <div className="flex items-baseline gap-1.5 min-w-0">
        <span className="text-[12px] font-medium text-foreground/90 truncate">
          {track.label}
        </span>
        <span className="text-[10px] font-mono text-muted-foreground shrink-0">
          {track.total}
        </span>
      </div>

      <div
        className="relative rounded bg-surface/50 border border-border/70 flex items-end overflow-hidden"
        style={{ height: `${TRACK_H}px` }}
        onMouseLeave={() => setHover(null)}
      >
        {track.buckets.map((bucket, i) => {
          const count = bucket.length;
          // Square-root scaling keeps small buckets visible next to a spike;
          // linear made everything but the peak invisible on bursty feeds.
          const ratio = track.peak > 0 ? Math.sqrt(count / track.peak) : 0;
          const h = count === 0 ? 0 : Math.max(3, ratio * (TRACK_H - 6));
          const isSel = selectedIndex === i;
          const isHov = hover === i;
          return (
            <button
              key={i}
              onMouseEnter={() => setHover(i)}
              onClick={() => count > 0 && onSelectBucket(i)}
              title={
                count === 0
                  ? undefined
                  : `${count} signal${count === 1 ? "" : "s"} · ${formatBucketRange(
                      minTs + i * bucketMs,
                      minTs + (i + 1) * bucketMs,
                    )}`
              }
              className="relative h-full shrink-0 flex items-end"
              style={{ width: `${100 / n}%`, cursor: count > 0 ? "pointer" : "default" }}
            >
              {/* Column highlight on hover / selection */}
              {(isHov || isSel) && (
                <span
                  className="absolute inset-0"
                  style={{
                    background: isSel
                      ? "color-mix(in oklab, var(--color-primary) 18%, transparent)"
                      : "oklch(1 0 0 / 0.05)",
                  }}
                />
              )}
              {count > 0 && (
                <span
                  className="relative w-full rounded-t-[2px]"
                  style={{
                    height: `${h}px`,
                    background: isSel ? "var(--color-primary)" : color,
                    opacity: isSel ? 1 : isHov ? 0.95 : 0.75,
                  }}
                />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Right-side panel: list of the selected bucket's signals, or one signal's
 *  full detail. Replaces the old floating tooltip, which covered neighboring
 *  tracks and could not be read for more than a moment. */
function SidePanel({
  title,
  rangeLabel,
  signals,
  selectedSignal,
  entityNames,
  onPick,
  onBack,
  onClose,
}: {
  title: string;
  rangeLabel: string;
  signals: Signal[];
  selectedSignal: Signal | null;
  entityNames: Record<string, string>;
  onPick: (id: string) => void;
  onBack: () => void;
  onClose: () => void;
}) {
  return (
    <div className="w-[360px] shrink-0 border-l border-border bg-surface/80 backdrop-blur flex flex-col">
      <div className="flex items-start justify-between gap-2 px-3 py-2 border-b border-border">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            {selectedSignal && (
              <button
                onClick={onBack}
                className="text-muted-foreground hover:text-foreground shrink-0"
                title="Back to list"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
              </button>
            )}
            <span className="text-[12px] font-semibold text-foreground truncate">
              {selectedSignal ? selectedSignal.type : title}
            </span>
          </div>
          <div className="text-[10px] font-mono text-muted-foreground mt-0.5">
            {selectedSignal
              ? new Date(selectedSignal.timestamp).toLocaleString()
              : `${rangeLabel} · ${signals.length} signal${signals.length === 1 ? "" : "s"}`}
          </div>
        </div>
        <button
          onClick={onClose}
          className="text-muted-foreground hover:text-foreground shrink-0"
          title="Close"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="flex-1 min-h-0 overflow-auto">
        {selectedSignal ? (
          <SignalDetail signal={selectedSignal} entityNames={entityNames} />
        ) : (
          <div className="divide-y divide-border/60">
            {signals.map((s) => (
              <button
                key={s.id}
                onClick={() => onPick(s.id)}
                className="w-full text-left px-3 py-2 hover:bg-surface-elevated"
              >
                <div className="flex items-center gap-1.5 mb-0.5">
                  <span
                    className="h-1.5 w-1.5 rounded-full shrink-0"
                    style={{ background: colorFor(s.type) }}
                  />
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground truncate">
                    {s.type}
                  </span>
                  <span className="ml-auto text-[9px] font-mono text-muted-foreground shrink-0">
                    {new Date(s.timestamp).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                </div>
                <div className="text-[12px] text-foreground/90 line-clamp-2">
                  {shortTitle(s)}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function SignalDetail({
  signal,
  entityNames,
}: {
  signal: Signal;
  entityNames: Record<string, string>;
}) {
  const attrs = signal.attributes ?? {};
  const title =
    (attrs.title as string) ||
    (attrs.headline as string) ||
    (attrs.article_title as string) ||
    signal.type;
  const summary =
    (attrs.summary as string) ||
    (attrs.description as string) ||
    (attrs.body as string) ||
    null;
  const source = (attrs.source as string) || (attrs.publisher as string) || null;
  const url = (attrs.url as string) || (attrs.article_url as string) || null;
  const structured = new Set([
    "title",
    "headline",
    "article_title",
    "summary",
    "description",
    "body",
    "source",
    "publisher",
    "url",
    "article_url",
  ]);
  const remaining = Object.entries(attrs).filter(
    ([k, v]) => !structured.has(k) && v !== null && v !== undefined && v !== "",
  );

  return (
    <div className="p-3 space-y-3 text-[12px]">
      <div>
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">
          Title
        </div>
        <div className="text-foreground font-medium leading-snug">{title}</div>
      </div>
      {source && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">
            Source
          </div>
          <div className="text-foreground/90 truncate">{source}</div>
        </div>
      )}
      {summary && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">
            Summary
          </div>
          <div className="text-foreground/85 leading-relaxed">{summary}</div>
        </div>
      )}
      {url && (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-primary hover:underline text-[11px]"
        >
          <ExternalLink className="h-3 w-3" />
          Open source
        </a>
      )}
      {signal.related_entity_ids.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
            Entities ({signal.related_entity_ids.length})
          </div>
          <div className="flex flex-wrap gap-1">
            {signal.related_entity_ids.map((id) => (
              <span
                key={id}
                className="text-[10px] px-1.5 py-0.5 rounded bg-muted/40 text-foreground/85 truncate max-w-[180px]"
              >
                {entityNames[id] ?? id.slice(0, 8)}
              </span>
            ))}
          </div>
        </div>
      )}
      {remaining.length > 0 && (
        <details>
          <summary className="text-[10px] uppercase tracking-wider text-muted-foreground cursor-pointer hover:text-foreground">
            Raw attributes ({remaining.length})
          </summary>
          <div className="mt-1.5 space-y-0.5">
            {remaining.map(([k, v]) => (
              <div key={k} className="text-[10px]">
                <span className="text-muted-foreground">{k}:</span>{" "}
                <span className="text-foreground/85 break-all">{String(v)}</span>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

function shortTitle(s: Signal): string {
  const a = s.attributes ?? {};
  return (
    (a.title as string) ||
    (a.headline as string) ||
    (a.article_title as string) ||
    (a.description as string) ||
    s.type
  );
}

/** Pick ~6 evenly spaced ticks and label them at a resolution that matches
 *  the span (time of day for short windows, dates for long ones). */
function buildAxisTicks(minTs: number, maxTs: number, widthPx: number) {
  const span = maxTs - minTs;
  const count = Math.max(3, Math.min(8, Math.floor(widthPx / 110)));
  const sameDay = span < 36 * 3600_000;
  const withinYear = span < 300 * 86400_000;
  const out: { ts: number; pct: number; label: string }[] = [];
  for (let i = 0; i <= count; i++) {
    const ts = minTs + (span * i) / count;
    const d = new Date(ts);
    const label = sameDay
      ? d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
      : withinYear
        ? d.toLocaleDateString([], { month: "short", day: "numeric" })
        : d.toLocaleDateString([], { month: "short", year: "2-digit" });
    // Nudge the first/last labels inward so they don't clip at the edges.
    const pct = (i / count) * 100;
    out.push({ ts, pct: Math.min(97, Math.max(3, pct)), label });
  }
  return out;
}

function formatBucketSpan(ms: number): string {
  if (ms < 3600_000) return `${Math.max(1, Math.round(ms / 60_000))}m`;
  if (ms < 86400_000) return `${Math.round(ms / 3600_000)}h`;
  return `${Math.max(1, Math.round(ms / 86400_000))}d`;
}

function formatBucketRange(startMs: number, endMs: number): string {
  const s = new Date(startMs);
  const e = new Date(endMs);
  const sameDay = s.toDateString() === e.toDateString();
  const d = (x: Date) => x.toLocaleDateString([], { month: "short", day: "numeric" });
  const t = (x: Date) => x.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return sameDay ? `${d(s)} ${t(s)}–${t(e)}` : `${d(s)} – ${d(e)}`;
}
